import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { nursingService, NursingAssessmentData, NursingNoteData } from '../../services/nursing';

interface NursingAssessment {
  id: number;
  patient_id: number;
  visit_id: number;
  patient_name: string;
  systolic_bp?: number;
  diastolic_bp?: number;
  temperature?: number;
  pulse_rate?: number;
  respiratory_rate?: number;
  height?: number;
  weight?: number;
  bmi?: number;
  bmi_category?: string;
  complaints?: string;
  nursing_notes?: string;
  assessment_datetime: string;
  vitals_summary?: any;
}

interface DoctorOrder {
  id: number;
  patient_id: number;
  patient_name: string;
  medical_record_number: string;
  doctor_name: string;
  order_type: string;
  order_type_name: string;
  order_description: string;
  medication_details?: string;
  priority: string;
  priority_name: string;
  status: string;
  status_name: string;
  scheduled_time?: string;
  is_overdue: boolean;
  time_remaining?: string;
}

interface NursingNote {
  id: number;
  patient_id: number;
  patient_name: string;
  note_type: string;
  note_type_name: string;
  note_content: string;
  observation_time: string;
  priority_level: string;
  priority_level_name: string;
  follow_up_required: boolean;
}

interface WorkloadSummary {
  total_patients: number;
  assessed_patients: number;
  pending_assessments: number;
  pending_orders: number;
  urgent_orders: number;
  abnormal_vitals: number;
  critical_notes: number;
}

interface NursingState {
  // Assessments
  assessments: NursingAssessment[];
  currentAssessment: NursingAssessment | null;
  
  // Doctor Orders
  doctorOrders: DoctorOrder[];
  
  // Nursing Notes
  nursingNotes: NursingNote[];
  
  // Patients
  patients: any[];
  
  // Workload
  workloadSummary: WorkloadSummary | null;
  
  // UI State
  loading: {
    assessments: boolean;
    orders: boolean;
    notes: boolean;
    patients: boolean;
    workload: boolean;
  };
  
  error: {
    assessments: string | null;
    orders: string | null;
    notes: string | null;
    patients: string | null;
    workload: string | null;
  };
  
  // Filters
  filters: {
    assessmentDate: string;
    orderStatus: string;
    orderPriority: string;
    noteType: string;
    notePriority: string;
  };
}

const initialState: NursingState = {
  assessments: [],
  currentAssessment: null,
  doctorOrders: [],
  nursingNotes: [],
  patients: [],
  workloadSummary: null,
  loading: {
    assessments: false,
    orders: false,
    notes: false,
    patients: false,
    workload: false,
  },
  error: {
    assessments: null,
    orders: null,
    notes: null,
    patients: null,
    workload: null,
  },
  filters: {
    assessmentDate: '',
    orderStatus: 'pending',
    orderPriority: '',
    noteType: '',
    notePriority: '',
  },
};

// Async Thunks
export const fetchPatients = createAsyncThunk(
  'nursing/fetchPatients',
  async (params?: any) => {
    const response = await nursingService.getPatients(params);
    // Handle different response structures
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (Array.isArray(response)) {
      return response;
    }
    return [];
  }
);

export const createAssessment = createAsyncThunk(
  'nursing/createAssessment',
  async (data: NursingAssessmentData) => {
    const response = await nursingService.storeAssessment(data);
    return response.data.assessment;
  }
);

export const updateAssessment = createAsyncThunk(
  'nursing/updateAssessment',
  async ({ id, data }: { id: number; data: Partial<NursingAssessmentData> }) => {
    const response = await nursingService.updateAssessment(id, data);
    return response.data.assessment;
  }
);

export const fetchDoctorOrders = createAsyncThunk(
  'nursing/fetchDoctorOrders',
  async (params?: any) => {
    const response = await nursingService.getDoctorOrders(params);
    
    // Backend returns { success: true, data: paginatedResult }
    // Laravel pagination returns { data: [], current_page, last_page, etc. }
    if (response.data && response.data.data) {
      // Handle Laravel pagination structure
      if (Array.isArray(response.data.data.data)) {
        return response.data.data.data; // Laravel paginated data
      } else if (Array.isArray(response.data.data)) {
        return response.data.data; // Direct array
      }
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response)) {
      return response;
    }
    
    return [];
  }
);

export const executeDoctorOrder = createAsyncThunk(
  'nursing/executeDoctorOrder',
  async ({ orderId, notes }: { orderId: number; notes?: string }) => {
    const response = await nursingService.executeDoctorOrder(orderId, notes);
    return response.data;
  }
);

export const updateDoctorOrderStatus = createAsyncThunk(
  'nursing/updateDoctorOrderStatus',
  async ({ orderId, status, notes }: { orderId: number; status: string; notes?: string }) => {
    const response = await nursingService.updateDoctorOrderStatus(orderId, status, notes);
    return response.data;
  }
);

export const fetchNursingNotes = createAsyncThunk(
  'nursing/fetchNursingNotes',
  async ({ patientId, params }: { patientId: number; params?: any }) => {
    const response = await nursingService.getNursingNotes(patientId, params);
    return response.data;
  }
);

export const createNursingNote = createAsyncThunk(
  'nursing/createNursingNote',
  async (data: NursingNoteData) => {
    const response = await nursingService.storeNursingNote(data);
    return response.data;
  }
);

export const fetchWorkloadSummary = createAsyncThunk(
  'nursing/fetchWorkloadSummary',
  async (date?: string) => {
    const response = await nursingService.getWorkloadSummary(date);
    return response.data;
  }
);

// Slice
const nursingSlice = createSlice({
  name: 'nursing',
  initialState,
  reducers: {
    // Filter actions
    setAssessmentDateFilter: (state, action: PayloadAction<string>) => {
      state.filters.assessmentDate = action.payload;
    },
    setOrderStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.orderStatus = action.payload;
    },
    setOrderPriorityFilter: (state, action: PayloadAction<string>) => {
      state.filters.orderPriority = action.payload;
    },
    setNoteTypeFilter: (state, action: PayloadAction<string>) => {
      state.filters.noteType = action.payload;
    },
    setNotePriorityFilter: (state, action: PayloadAction<string>) => {
      state.filters.notePriority = action.payload;
    },
    
    // Clear actions
    clearAssessments: (state) => {
      state.assessments = [];
      state.currentAssessment = null;
    },
    clearDoctorOrders: (state) => {
      state.doctorOrders = [];
    },
    clearNursingNotes: (state) => {
      state.nursingNotes = [];
    },
    clearErrors: (state) => {
      state.error = {
        assessments: null,
        orders: null,
        notes: null,
        patients: null,
        workload: null,
      };
    },
    
    // Set current assessment
    setCurrentAssessment: (state, action: PayloadAction<NursingAssessment | null>) => {
      state.currentAssessment = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Patients
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading.patients = true;
        state.error.patients = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading.patients = false;
        state.patients = action.payload;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading.patients = false;
        state.error.patients = action.error.message || 'Failed to fetch patients';
      });

    // Create Assessment
    builder
      .addCase(createAssessment.pending, (state) => {
        state.loading.assessments = true;
        state.error.assessments = null;
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.loading.assessments = false;
        state.assessments.unshift(action.payload);
        state.currentAssessment = action.payload;
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.loading.assessments = false;
        state.error.assessments = action.error.message || 'Failed to create assessment';
      });

    // Update Assessment
    builder
      .addCase(updateAssessment.fulfilled, (state, action) => {
        const index = state.assessments.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.assessments[index] = action.payload;
        }
        state.currentAssessment = action.payload;
      });

    // Fetch Doctor Orders
    builder
      .addCase(fetchDoctorOrders.pending, (state) => {
        state.loading.orders = true;
        state.error.orders = null;
      })
      .addCase(fetchDoctorOrders.fulfilled, (state, action) => {
        state.loading.orders = false;
        state.doctorOrders = action.payload;
      })
      .addCase(fetchDoctorOrders.rejected, (state, action) => {
        state.loading.orders = false;
        state.error.orders = action.error.message || 'Failed to fetch doctor orders';
      });

    // Execute Doctor Order
    builder
      .addCase(executeDoctorOrder.fulfilled, (state, action) => {
        const index = state.doctorOrders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.doctorOrders[index] = { ...state.doctorOrders[index], ...action.payload };
        }
      });

    // Update Doctor Order Status
    builder
      .addCase(updateDoctorOrderStatus.fulfilled, (state, action) => {
        const index = state.doctorOrders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.doctorOrders[index] = { ...state.doctorOrders[index], ...action.payload };
        }
      });

    // Fetch Nursing Notes
    builder
      .addCase(fetchNursingNotes.pending, (state) => {
        state.loading.notes = true;
        state.error.notes = null;
      })
      .addCase(fetchNursingNotes.fulfilled, (state, action) => {
        state.loading.notes = false;
        state.nursingNotes = action.payload;
      })
      .addCase(fetchNursingNotes.rejected, (state, action) => {
        state.loading.notes = false;
        state.error.notes = action.error.message || 'Failed to fetch nursing notes';
      });

    // Create Nursing Note
    builder
      .addCase(createNursingNote.fulfilled, (state, action) => {
        state.nursingNotes.unshift(action.payload);
      });

    // Fetch Workload Summary
    builder
      .addCase(fetchWorkloadSummary.pending, (state) => {
        state.loading.workload = true;
        state.error.workload = null;
      })
      .addCase(fetchWorkloadSummary.fulfilled, (state, action) => {
        state.loading.workload = false;
        state.workloadSummary = action.payload;
      })
      .addCase(fetchWorkloadSummary.rejected, (state, action) => {
        state.loading.workload = false;
        state.error.workload = action.error.message || 'Failed to fetch workload summary';
      });
  },
});

export const {
  setAssessmentDateFilter,
  setOrderStatusFilter,
  setOrderPriorityFilter,
  setNoteTypeFilter,
  setNotePriorityFilter,
  clearAssessments,
  clearDoctorOrders,
  clearNursingNotes,
  clearErrors,
  setCurrentAssessment,
} = nursingSlice.actions;

export default nursingSlice.reducer;